import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Upload, Link as LinkIcon, X } from 'lucide-react';
import { getArtists } from '../../../services/api';

/**
 * ReleaseForm - Form component for creating/editing releases with multi-artist selection
 */
export const ReleaseForm = ({ 
  formData, 
  handleChange, 
  handleFileUpload,
  handleBackCoverUpload,
  uploading,
  uploadingBack,
  imagePreview,
  backCoverPreview,
  setFormData
}) => {
  const [artists, setArtists] = useState([]);
  const [selectedArtists, setSelectedArtists] = useState(formData.artists || []);

  const fetchArtists = useCallback(async () => {
    try {
      const response = await getArtists();
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    }
  }, []);

  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  useEffect(() => {
    if (formData.artists) {
      setSelectedArtists(formData.artists);
    }
  }, [formData.artists]);

  const handleArtistToggle = (artistName) => {
    let updatedArtists;
    if (selectedArtists.includes(artistName)) {
      updatedArtists = selectedArtists.filter(a => a !== artistName);
    } else {
      updatedArtists = [...selectedArtists, artistName];
    }
    setSelectedArtists(updatedArtists);
    setFormData({ ...formData, artists: updatedArtists });
  };

  const removeArtist = (artistName) => {
    const updatedArtists = selectedArtists.filter(a => a !== artistName);
    setSelectedArtists(updatedArtists);
    setFormData({ ...formData, artists: updatedArtists });
  };

  return (
    <div className="space-y-4 pb-4">
      {/* Basic Info Section */}
      <div className="border-b border-green-500/20 pb-4">
        <h3 className="text-lg font-semibold mb-4 text-green-400">Release Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-2">Album Name *</label>
            <Input 
              name="title" 
              value={formData.title} 
              onChange={handleChange} 
              required 
              className="bg-black/50 border-green-500/30" 
              placeholder="Enter album title" 
            />
          </div>
          
          {/* Multi-Artist Selection */}
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-2">Artists *</label>
            <div className="bg-black/50 border border-green-500/30 rounded-md p-3 min-h-[50px]">
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedArtists.map((artistName) => (
                  <Badge 
                    key={artistName}
                    variant="secondary"
                    className="bg-orange-500/20 text-orange-300 border-orange-500/50 pr-1"
                  >
                    {artistName}
                    <button
                      type="button"
                      onClick={() => removeArtist(artistName)}
                      className="ml-2 hover:bg-orange-500/30 rounded-full p-0.5"
                    >
                      <X size={14} />
                    </button>
                  </Badge>
                ))}
              </div>
              <Select onValueChange={handleArtistToggle}>
                <SelectTrigger className="bg-black/30 border-green-500/20">
                  <SelectValue placeholder="Select artists..." />
                </SelectTrigger>
                <SelectContent className="bg-black border-green-500/30 text-white max-h-60">
                  {artists.map((artist) => (
                    <SelectItem key={artist.id} value={artist.name}>
                      <div className="flex items-center justify-between w-full">
                        <span>{artist.name}</span>
                        {selectedArtists.includes(artist.name) && (
                          <Badge className="ml-2 bg-green-500/20 text-green-400">✓</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedArtists.length === 0 && (
              <p className="text-xs text-red-400 mt-1">Please select at least one artist</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Genre</label>
            <Input 
              name="genre" 
              value={formData.genre} 
              onChange={handleChange} 
              className="bg-black/50 border-green-500/30" 
              placeholder="e.g., Psytrance, Progressive" 
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Release Date (YYYY-MM) *</label>
            <Input 
              name="releaseDate" 
              value={formData.releaseDate} 
              onChange={handleChange} 
              required 
              className="bg-black/50 border-green-500/30" 
              placeholder="2024-12" 
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-2">Type</label>
            <Select 
              value={formData.type} 
              onValueChange={(value) => setFormData({ ...formData, type: value })}
            >
              <SelectTrigger className="bg-black/50 border-green-500/30">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-black border-green-500/30">
                <SelectItem value="EP">EP</SelectItem>
                <SelectItem value="Album">Album</SelectItem>
                <SelectItem value="Single">Single</SelectItem>
                <SelectItem value="Compilation">Compilation</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Cover Art Section */}
      <div className="border-b border-orange-500/20 pb-4">
        <h3 className="text-lg font-semibold mb-4 text-orange-400">Cover Artwork</h3>
        
        {/* Front Cover */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Front Cover *</label>
          <Tabs defaultValue="url" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-black/50">
              <TabsTrigger value="url"><LinkIcon className="w-4 h-4 mr-2" /> URL</TabsTrigger>
              <TabsTrigger value="upload"><Upload className="w-4 h-4 mr-2" /> Upload</TabsTrigger>
            </TabsList>
            <TabsContent value="url" className="space-y-2">
              <Input 
                name="artwork" 
                value={formData.artwork} 
                onChange={handleChange} 
                placeholder="https://example.com/image.jpg"
                className="bg-black/50 border-green-500/30" 
              />
            </TabsContent>
            <TabsContent value="upload" className="space-y-2">
              <Input 
                type="file" 
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="bg-black/50 border-green-500/30" 
              />
              {uploading && <p className="text-sm text-orange-400">Uploading...</p>}
            </TabsContent>
          </Tabs>
          {imagePreview && (
            <div className="mt-2">
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="w-full h-48 object-cover rounded border border-green-500/30" 
              />
            </div>
          )}
        </div>
        
        {/* Back Cover */}
        <div>
          <label className="block text-sm font-medium mb-2">Back Cover</label>
          <Tabs defaultValue="url" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-black/50">
              <TabsTrigger value="url"><LinkIcon className="w-4 h-4 mr-2" /> URL</TabsTrigger>
              <TabsTrigger value="upload"><Upload className="w-4 h-4 mr-2" /> Upload</TabsTrigger>
            </TabsList>
            <TabsContent value="url" className="space-y-2">
              <Input 
                name="backCover" 
                value={formData.backCover} 
                onChange={handleChange} 
                placeholder="https://example.com/back-cover.jpg"
                className="bg-black/50 border-green-500/30" 
              />
            </TabsContent>
            <TabsContent value="upload" className="space-y-2">
              <Input 
                type="file" 
                accept="image/*"
                onChange={handleBackCoverUpload}
                disabled={uploadingBack}
                className="bg-black/50 border-green-500/30" 
              />
              {uploadingBack && <p className="text-sm text-orange-400">Uploading...</p>}
            </TabsContent>
          </Tabs>
          {backCoverPreview && (
            <div className="mt-2">
              <img 
                src={backCoverPreview} 
                alt="Back Cover Preview" 
                className="w-full h-48 object-cover rounded border border-green-500/30" 
              />
            </div>
          )}
        </div>
      </div>

      {/* Description Section */}
      <div className="border-b border-green-500/20 pb-4">
        <h3 className="text-lg font-semibold mb-4 text-green-400">Release Description</h3>
        <Textarea 
          name="description" 
          value={formData.description} 
          onChange={handleChange} 
          rows={4}
          placeholder="Tell us about this release..."
          className="bg-black/50 border-green-500/30 resize-none" 
        />
      </div>

      {/* Links & Streaming Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-orange-400">Streaming Links</h3>
        
        <div>
          <label className="block text-sm font-medium mb-2">SoundCloud URL</label>
          <Input 
            name="soundcloudUrl" 
            value={formData.soundcloudUrl} 
            onChange={handleChange} 
            className="bg-black/50 border-green-500/30" 
            placeholder="https://soundcloud.com/..." 
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Bandcamp URL</label>
          <Input 
            name="bandcampUrl" 
            value={formData.bandcampUrl} 
            onChange={handleChange} 
            className="bg-black/50 border-green-500/30" 
            placeholder="https://artist.bandcamp.com/album/..." 
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Bandcamp Embed Code</label>
          <Textarea 
            name="bandcampEmbed" 
            value={formData.bandcampEmbed} 
            onChange={handleChange} 
            rows={3}
            placeholder='Paste embed code from Bandcamp: <iframe src="..." ...></iframe>'
            className="bg-black/50 border-green-500/30 resize-none text-xs" 
          />
          <p className="text-xs text-gray-500 mt-1">📎 Get from album page → Share/Embed → Copy iframe code</p>
        </div>
      </div>
    </div>
  );
};
